/** One undeliverable address: its state, the why-ledger, and Release.
 *
 * The ledger (bounce events) is the evidence behind the state row —
 * the page an operator reads BEFORE deciding a release is safe, which
 * is why the release action lives here and not on the list.
 *
 * Release is deliberately calm about mistakes: if the address is still
 * dead, the very next send bounces and the webhook re-activates the
 * row. The dialog says so, because "am I allowed to try this?" is the
 * operator's actual question.
 */
import { SectionHeader } from '../common/section-header'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import {
  keysStaleAfterSuppressionRelease,
  queryKeys,
} from '../api/query-keys'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Icon,
  Notice,
} from '@speaking-coach/ui'
import { PageHeader } from '../components/page-header'
import { OPERATOR_PAGE_MAIN } from '../common/page-layout'
import { PendingSpinner } from '../common/save-button'
import { HighConsequenceActionPanel } from '../common/high-consequence-action-panel'
import { TABLE, TABLE_HEAD_ROW, TABLE_ROW } from '../common/table'
import { adminEmailSuppressionsApi, messageForSuppressionError } from './api'
import { formatWhen, reasonProse, shortHash } from './format'
import type { SuppressionRow, SuppressionUser } from './types'

const EVENT_PROSE: Record<string, string> = {
  hard_bounce: 'Hard bounce',
  soft_bounce: 'Soft bounce',
  spam: 'Spam complaint',
  unsub: 'Unsubscribe',
  reject: 'Rejected before sending',
}

function eventProse(eventType: string): string {
  return EVENT_PROSE[eventType] ?? eventType
}

/** What the row is ABOUT, for headings and the release dialog: the
 * resolved address when a live user matches, the digest otherwise. */
function recipientLabel(suppression: SuppressionRow): string {
  return suppression.user !== null
    ? suppression.user.email
    : `address ${shortHash(suppression.emailHash)}`
}

function roleLabel(role: string): string {
  if (role === 'hr') return 'HR user'
  if (role === 'admin') return 'Operator'
  return 'Student'
}

/** The digest join resolves students, HR users and Cooori admins alike, so
 * the link target is decided per role rather than assuming a student page. */
function PersonLink({ user }: { user: SuppressionUser }): JSX.Element {
  if (user.role === 'hr') {
    return (
      <Link
        to={`/admin/clients/${user.clientId}/hr-users/${user.userId}`}
        className="underline"
      >
        {user.name}
      </Link>
    )
  }
  if (user.role === 'admin') {
    return (
      <Link to={`/admin/admins/${user.userId}`} className="underline">
        {user.name}
      </Link>
    )
  }
  return (
    <Link to={`/admin/students/${user.userId}/edit`} className="underline">
      {user.name}
    </Link>
  )
}

export function SuppressionDetailPage(): JSX.Element {
  const { emailHash = '' } = useParams()
  const queryClient = useQueryClient()

  const detailQuery = useQuery({
    queryKey: queryKeys.emailSuppression(emailHash),
    queryFn: () => adminEmailSuppressionsApi.get(emailHash),
  })

  const release = useMutation({
    mutationFn: () => adminEmailSuppressionsApi.release(emailHash),
    onSuccess: () => {
      // The helper owns WHAT goes stale (the suppression's own views
      // plus the resolved person's role-keyed detail) so the answer is
      // a property of the data, not of this page.
      for (const key of keysStaleAfterSuppressionRelease(
        emailHash,
        detailQuery.data?.suppression.user,
      )) {
        queryClient.invalidateQueries({ queryKey: key })
      }
    },
  })

  const detail = detailQuery.data
  const suppression = detail?.suppression
  const isActive = suppression !== undefined && suppression.releasedAt === null

  return (
    <main className={OPERATOR_PAGE_MAIN}>
      <div className="max-w-2xl mx-auto">
        {detailQuery.isPending && (
          <p className="text-text-secondary mt-4">Loading address…</p>
        )}
        {detailQuery.isError && (
          <div
            role="alert"
            className="mt-4 bg-surface-card border border-danger/40 p-4 rounded-token-card"
          >
            <p className="font-medium">Could not load this address</p>
            <p className="text-text-secondary text-body mt-1">
              {messageForSuppressionError(detailQuery.error)}
            </p>
          </div>
        )}

        {detail !== undefined && suppression !== undefined && (
          <>
            <PageHeader
              title={
                suppression.user !== null ? (
                  suppression.user.email
                ) : (
                  <span className="font-mono" title={suppression.emailHash}>
                    {shortHash(suppression.emailHash)}
                  </span>
                )
              }
            />

            <div className="mt-3">
              {isActive ? (
                <Notice variant="warning">
                  Undeliverable since {formatWhen(suppression.suppressedAt)} —{' '}
                  {reasonProse(suppression.reason).toLowerCase()}. New sends to
                  this address are skipped.
                </Notice>
              ) : (
                <Notice variant="neutral">
                  Released {formatWhen(suppression.releasedAt)}. Deliveries to
                  this address are allowed again; a new bounce would re-list
                  it automatically.
                </Notice>
              )}
            </div>

            <section
              aria-label="Recipient"
              className="mt-6 bg-surface-card border border-border-default rounded-token-card p-4"
            >
              <SectionHeader title="Recipient" />
              {suppression.user !== null ? (
                <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-2 text-body">
                  <div>
                    <dt className="text-text-secondary">
                      {roleLabel(suppression.user.role)}
                    </dt>
                    <dd>
                      <PersonLink user={suppression.user} />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-text-secondary">Client</dt>
                    <dd>
                      <Link
                        to={`/admin/clients/${suppression.user.clientId}`}
                        className="underline"
                      >
                        {suppression.user.clientName ??
                          suppression.user.clientId}
                      </Link>
                    </dd>
                  </div>
                </dl>
              ) : (
                <p className="mt-2 text-body text-text-secondary">
                  No current user has this address. Only its fingerprint is
                  stored, so the address itself cannot be displayed — it may
                  belong to a deleted user or to a typo that bounced.
                </p>
              )}
            </section>

            <section aria-label="Bounce history" className="mt-6">
              <SectionHeader
                title={<>Why this address is listed{' '}
                <span className="text-text-secondary font-normal">
                  ({detail.events.length}{' '}
                  {detail.events.length === 1 ? 'event' : 'events'})
                </span></>}
              />
              {detail.events.length === 0 ? (
                <p className="mt-2 text-body text-text-secondary">
                  No webhook events recorded — the row predates the ledger or
                  was created manually.
                </p>
              ) : (
                <table className={`${TABLE} mt-2`}>
                  <thead>
                    <tr className={TABLE_HEAD_ROW}>
                      <th className="p-3 font-medium">When</th>
                      <th className="p-3 font-medium">Event</th>
                      <th className="p-3 font-medium">Bounce code</th>
                      <th className="p-3 font-medium">Email kind</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.events.map((event, index) => (
                      <tr
                        key={`${event.eventAt}-${index}`}
                        className={TABLE_ROW}
                      >
                        <td className="p-3 text-text-secondary">
                          {formatWhen(event.eventAt)}
                        </td>
                        <td className="p-3">{eventProse(event.eventType)}</td>
                        <td className="p-3 text-text-secondary">
                          {event.bounceCode ?? '—'}
                          {event.eventType === 'soft_bounce' &&
                            !event.countable && (
                              <span className="block text-table-secondary">
                                environmental — not counted toward escalation
                              </span>
                            )}
                        </td>
                        <td className="p-3 text-text-secondary">
                          {event.kind ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            {isActive && (
              <HighConsequenceActionPanel
                title="Release this address"
                className="mt-8"
                description={
                  <>
                    Allows deliveries again — for when the mailbox itself
                    recovered: a full inbox emptied, a broken company domain
                    repaired. A misspelled address cannot be corrected here or
                    on the student — deactivate the student and re-create them
                    with the right address instead; this entry only blocks the
                    misspelled one, so no release is needed. If the address
                    still bounces, the next send re-lists it automatically.
                  </>
                }
                error={
                  release.isError
                    ? messageForSuppressionError(release.error)
                    : undefined
                }
              >
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      type="button"
                      variant="dangerOpen"
                      pending={release.isPending}
                      disabled={release.isPending}
                    >
                      {release.isPending ? (
                        <>
                          <PendingSpinner surface="card" />
                          Releasing…
                        </>
                      ) : (
                        <>
                          <Icon name="restore" />
                          Release address
                        </>
                      )}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Release {recipientLabel(suppression)}?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        Emails will be delivered to it again, starting with
                        the next send. If the mailbox still refuses mail, the
                        bounce re-lists the address automatically — the cost
                        of a wrong release is one more bounce.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogAction
                        onClick={() => release.mutate()}
                        disabled={release.isPending}
                      >
                        Yes, release
                      </AlertDialogAction>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </HighConsequenceActionPanel>
            )}
            {!isActive && release.isSuccess && (
              <p role="status" className="mt-6 text-body">
                Address released. Deliveries can reach it again.
              </p>
            )}
          </>
        )}
      </div>
    </main>
  )
}
